import * as _ from 'underscore';
import { FeatureFlags } from '../FeatureFlags';
import { PolicyNames } from '../models/user/validation/IPasswordPolicy';
import { LocalAuthConfiguration } from '../models/user/validation/LocalAuthConfiguration';
import { Module } from '../Module';
import { GetLocalAuthConfigurationRequest } from '../ServerApi/requests/GetLocalAuthConfigurationRequest';
import { ServerService } from './ServerService';

export interface PasswordPolicyScope extends ng.IScope {
  passwordPolicyValidation: {
    shouldShowErrorMessages: () => boolean;
    mustContainLetters: () => boolean;
    mustContainUppercaseLetters: () => boolean;
    mustContainNumbers: () => boolean;
    mustContainSymbols: () => boolean;
    mustHaveMinimumPasswordLength: () => boolean;
    mustHaveMaximumPasswordLength: () => boolean;
    respectsPolicy: (policyName: PolicyNames) => boolean;
    respectsAllPolicies: () => boolean;
    getMinLength: () => number;
    getMaxLength: () => number;
  };
}

export class PasswordPolicyService {

  private server: ServerService;

  public static $inject = ['ServerService'];

  constructor(server: ServerService) {
    this.server = server;
  }

  // Enhances the scope to include the functionality introduced by the passwordPoliciesValidation mixin.
  // Receives the current scope, a getter for the password being introduced, and a callback to signal when the configuration
  // was correctly loaded from the server. Until that point, the validation would be turned off.
  // Returns a function that will provide the result of the validation applied to the new password accessor provided.
  public loadPoliciesIntoScope(scope: PasswordPolicyScope, getNewPassword: () => string,
    configurationLoadedCallback: () => void): () => boolean {

    // If the feature is disabled, we leave the scope as is and return that any password is valid.
    if (!FeatureFlags.passwordPoliciesEnabled) {
      configurationLoadedCallback();
      return () => true;
    }

    let currentConfig = LocalAuthConfiguration.default();

    // We start with an empty config and replace it when the server replies
    this.fetchConfiguration().then(function(serverConfig) {
      currentConfig = serverConfig;
      configurationLoadedCallback();
    });

    scope.passwordPolicyValidation = {
      shouldShowErrorMessages: (): boolean => {
        return getNewPassword() && getNewPassword().length > 0;
      },
      respectsPolicy: (policyName: PolicyNames): boolean => {
        const activePolicies = currentConfig.getActivePasswordPolicies();
        const newPassword = getNewPassword();
        return !activePolicies.has(policyName) || activePolicies.get(policyName).isValid(newPassword);
      },
      respectsAllPolicies: (): boolean => {
        let ans: boolean = true;
        const newPassword = getNewPassword();
        currentConfig.getActivePasswordPolicies().forEach(function(policy) {
          ans = ans && policy.isValid(newPassword);
        });
        return ans;
      },
      mustContainLetters: (): boolean => {
        return scope.passwordPolicyValidation.respectsPolicy(PolicyNames.MustContainLetters);
      },
      mustContainUppercaseLetters: (): boolean => {
        return scope.passwordPolicyValidation.respectsPolicy(PolicyNames.MustContainUppercaseLetters);
      },
      mustContainNumbers: (): boolean => {
        return scope.passwordPolicyValidation.respectsPolicy(PolicyNames.MustContainNumbers);
      },
      mustContainSymbols: (): boolean => {
        return scope.passwordPolicyValidation.respectsPolicy(PolicyNames.MustContainSymbols);
      },
      mustHaveMinimumPasswordLength: (): boolean => {
        return scope.passwordPolicyValidation.respectsPolicy(PolicyNames.MinimumPasswordLength);
      },
      mustHaveMaximumPasswordLength: (): boolean => {
        return scope.passwordPolicyValidation.respectsPolicy(PolicyNames.MaximumPasswordLength);
      },
      getMinLength: (): number => {
        return currentConfig.getMinPasswordLength();
      },
      getMaxLength: (): number => {
        return currentConfig.getMaxPasswordLength();
      },
    };
    return scope.passwordPolicyValidation.respectsAllPolicies;
  }

  private fetchConfiguration(): ng.IPromise<LocalAuthConfiguration> {
    return this.server.sendRequest(new GetLocalAuthConfigurationRequest()).then((result) => {
        return LocalAuthConfiguration.loadServerConfig(result);
    });
  }
}

Module.instance().service('PasswordPolicyService', PasswordPolicyService);
